import Navigation from "@/components/Navigation";
import YouTubeFacade from "@/components/YouTubeFacade";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle, GraduationCap, Globe, Utensils, Users, Heart, Award } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import claraPortrait from "@/assets/clara-portrait-final.png";
import heroBackground from "@/assets/hero-background.jpg";

export const faqItems = [
  {
    question: "Você atende presencialmente ou só online?",
    answer: "Atendo das duas formas: presencialmente no consultório em Hamilton, em Brisbane, e online para qualquer lugar da Austrália. A qualidade do acompanhamento é a mesma nos dois formatos."
  },
  {
    question: "Como funciona a primeira consulta?",
    answer: "É uma conversa de cerca de 30 minutos, sem custo, por videochamada ou no consultório em Hamilton. Eu entendo seus objetivos e sua rotina e explico como funciona o acompanhamento. Depois você decide, com calma, se quer seguir."
  },
  {
    question: "Preciso falar inglês para o atendimento?",
    answer: "Não. Todo o atendimento é em português: consultas, plano alimentar, receitas e mensagens."
  },
  {
    question: "Você usa os produtos que encontro aqui na Austrália?",
    answer: "Sim. Todos os planos são feitos com produtos que você encontra nos mercados daqui, como Coles, Woolworths e Aldi, sem ingredientes que só existem no Brasil."
  },
  {
    question: "Qual a diferença entre o seu acompanhamento e uma dieta comum?",
    answer: "Uma dieta comum é uma lista de regras. O acompanhamento inclui plano alimentar personalizado, acesso à plataforma, contato direto comigo por WhatsApp e ajustes conforme a sua rotina muda."
  },
  {
    question: "Com que frequência são as consultas?",
    answer: "Depende dos seus objetivos e da fase do acompanhamento. No início, os encontros costumam ser mais frequentes. Entre as consultas, você tem suporte contínuo pela plataforma e pelo WhatsApp."
  },
  {
    question: "Posso cancelar a qualquer momento?",
    answer: "Sim, sem multa e sem burocracia. É só me avisar."
  },
  {
    question: "Quanto tempo leva para ver resultados?",
    answer: "Os acompanhamentos normalmente têm um período mínimo de 3 meses, mas o tempo para ver resultados depende de cada pessoa e de cada plano. Muitos pacientes relatam, já nas primeiras semanas, mais disposição e uma relação mais tranquila com a comida."
  },
  {
    question: "Não moro em Brisbane. Posso ser atendida?",
    answer: "Sim. Atendo online pacientes de Sydney, Melbourne, Perth, Adelaide, Gold Coast e de qualquer outra cidade da Austrália. A consulta online funciona tão bem quanto a presencial."
  },
];

const beneficios = [
  "Plano alimentar personalizado com produtos dos mercados australianos",
  "Acesso à plataforma, com conteúdo para o dia a dia",
  "Contato direto comigo por WhatsApp entre as consultas",
  "Ajustes no plano conforme a sua rotina muda",
  "Suporte contínuo para manter a constância",
  "Acompanhamento de exames e evolução",
];

const paraQuem = [
  {
    icon: Globe,
    title: "Brasileiros que moram na Austrália",
    description: "E querem cuidar da alimentação com alguém que conhece a realidade de viver longe do Brasil."
  },
  {
    icon: Utensils,
    title: "Quem quer emagrecer de forma saudável",
    description: "Sem dietas extremas e sem passar fome, com um plano que funciona na sua rotina."
  },
  {
    icon: Users,
    title: "Quem sente falta de acompanhamento em português",
    description: "Sem precisar traduzir sintomas ou explicar hábitos alimentares em inglês."
  },
  {
    icon: Heart,
    title: "Quem quer manter a constância sem sofrer",
    description: "Se você já recomeçou muitas vezes, o acompanhamento ajuda a construir uma rotina alimentar que você consegue manter."
  },
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button
      size="lg"
      variant="gradient"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      style={{ background: 'var(--gradient-green)' }}
      onClick={openDialog}
    >
      <Calendar className="mr-2 h-5 w-5" />
      Agendar consulta
    </Button>
    <Button
      size="lg"
      variant="gradient-outline"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
      onClick={() => window.open('https://wa.me/61423038977', '_blank')}
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          {/* ===== HERO ===== */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                Clara Camille
              </p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
                Nutricionista brasileira na Austrália, atendimento em português
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
                Acompanhamento nutricional completo para brasileiros que moram na Austrália. Consultas presenciais em Brisbane ou online, de qualquer cidade do país.
              </p>
              <CTAButtons openDialog={openDialog} />
              <p className="text-sm text-muted-foreground font-sans">
                Consulta inicial de 30 minutos, sem custo.
              </p>
            </div>
          </section>

          {/* ===== PARA QUEM ===== */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Quem eu atendo
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Para quem é meu acompanhamento
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                {paraQuem.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ===== COMO FUNCIONA ===== */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Passo a passo
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Como funciona o atendimento na Austrália
                </h2>
              </div>

              <div className="space-y-6 sm:space-y-8">
                {[
                  { step: "1", title: "Agende a consulta inicial", desc: "Escolha o melhor horário pelo Calendly ou mande uma mensagem no WhatsApp. A consulta pode ser presencial em Brisbane ou online." },
                  { step: "2", title: "Vamos conversar sobre você", desc: "Na primeira consulta, conheço sua rotina, seus objetivos, sua relação com a comida e o que você já tentou antes. Nesse momento, meu papel é ouvir." },
                  { step: "3", title: "Receba seu plano personalizado", desc: "Com base na nossa conversa, monto um plano alimentar feito para a sua vida aqui na Austrália, com produtos dos mercados locais e opções práticas para o dia a dia." },
                  { step: "4", title: "Acompanhamento contínuo", desc: "Você tem acesso à plataforma, contato direto comigo por WhatsApp e ajustes no plano sempre que precisar. O foco é construir hábitos que se mantêm ao longo do tempo." },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 sm:gap-6 items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-typewriter font-bold text-lg sm:text-xl text-primary-foreground" style={{ background: 'var(--gradient-green)' }}>
                      {item.step}
                    </div>
                    <div className="space-y-1 sm:space-y-2 pt-1">
                      <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* ===== O QUE MUDA ===== */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  A realidade do imigrante
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground leading-tight">
                  O que muda quando você mora fora e como eu posso ajudar
                </h2>
              </div>

              {/* Video */}
              <div className="relative w-full aspect-video rounded-xl sm:rounded-2xl overflow-hidden mb-10 sm:mb-12">
                <YouTubeFacade
                  videoId="Cl5BqI_sk7U"
                  title="Clara Camille conta sua história como nutricionista brasileira na Austrália"
                  className="absolute inset-0 w-full h-full rounded-xl sm:rounded-2xl"
                />
              </div>

              <div className="mt-8 sm:mt-10">
                <CTAButtons openDialog={openDialog} />
              </div>

              <div className="space-y-6 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-3xl mx-auto mt-10 sm:mt-12">
                <p>
                  Quando você se muda para a Austrália, muita coisa muda: os horários, os produtos no mercado, os nomes dos alimentos, o clima, a rotina. E a alimentação costuma ser uma das primeiras coisas a sair do controle.
                </p>
                <p>
                  Você passa a comer o que é mais fácil, mais barato ou mais familiar. Com o tempo, percebe que ganhou peso, que está mais cansada, que a pele mudou e o intestino desregulou. E não sabe por onde começar, porque tudo parece complicado demais.
                </p>
                <p>
                  Eu entendo isso porque também passei por essa adaptação. Por isso, meu acompanhamento é pensado para quem vive essa realidade: monto o seu plano com os produtos que você encontra no Coles e no Woolworths, respeitando seu orçamento, seus horários e seus gostos.
                </p>
                <p>
                  Em vez de uma dieta genérica, um plano feito para a <span className="italic gradient-text font-medium">sua</span> vida.
                </p>
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* ===== O QUE VOCÊ RECEBE ===== */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  O que está incluído
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  O que você recebe no acompanhamento
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
                {beneficios.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* ===== CREDENCIAIS ===== */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Quem te acompanha
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Conheça a Clara Camille
                </h2>
              </div>

              <div
                className="rounded-2xl sm:rounded-3xl overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(16px) saturate(1.3)',
                  WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
                }}
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Photo */}
                  <div className="sm:w-2/5 flex-shrink-0">
                    <div className="aspect-[3/4] sm:h-full w-full overflow-hidden">
                      <img
                        src={claraPortrait}
                        alt="Clara Camille, nutricionista brasileira na Austrália"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="sm:w-3/5 p-6 sm:p-10 flex flex-col justify-center space-y-6">
                    <div className="space-y-1">
                      <h3 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">
                        Clara Camille
                      </h3>
                      <p className="text-sm sm:text-base text-primary font-typewriter font-semibold">
                        Nutricionista · Austrália
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {[
                        { icon: GraduationCap, text: "Formação em Nutrição" },
                        { icon: Globe, text: "Atendimento em português" },
                        { icon: Award, text: "Foco em brasileiros que vivem na Austrália" },
                      ].map((cred, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl bg-primary/5">
                          <cred.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <span className="text-xs sm:text-sm text-foreground font-sans leading-snug">{cred.text}</span>
                        </div>
                      ))}
                    </div>

                    <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                      Meu trabalho é ajudar brasileiros que moram na Austrália a cuidar da alimentação de um jeito que faça sentido na rotina de cada um. Tudo em português, com empatia e sem julgamento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== FAQ ===== */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Tire suas dúvidas
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Dúvidas frequentes
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="rounded-xl border border-border/50 bg-card px-5 sm:px-6"
                  >
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* ===== CTA FINAL ===== */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div
              className="absolute inset-0 blur-sm scale-105"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div
              className="absolute inset-0 opacity-80"
              style={{ background: 'var(--gradient-green)' }}
            />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div
                className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">
                  Mora na Austrália? Vamos conversar
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">
                  Na consulta inicial, entendo sua rotina e seus objetivos e explico como o acompanhamento funciona, presencial em Brisbane ou online, de onde você estiver.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                    onClick={openDialog}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Agendar consulta
                  </Button>
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(12px)',
                      color: 'white',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onClick={() => window.open('https://wa.me/61423038977', '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* ===== LINKS INTERNOS ===== */}
          <section className="bg-card py-10 sm:py-14">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center space-y-4">
              <p className="text-sm text-muted-foreground font-sans">
                Navegue pelo site:
              </p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                <Link to="/" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">
                  Página inicial
                </Link>
                <span className="text-border">·</span>
                <Link to="/" hash="about" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">
                  Minha história
                </Link>
                <span className="text-border">·</span>
                <Link to="/" hash="programs" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">
                  Como funciona
                </Link>
                <span className="text-border">·</span>
                <Link to="/" hash="testimonials" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">
                  Depoimentos
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const NutricionistaBrasileiraAustralia = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default NutricionistaBrasileiraAustralia;
